/**
 * =============================================================================
 * RISK CARD LAYOUT CANON (immutable UI contract)
 * =============================================================================
 *
 * PURPOSE
 *   Keep Risk #1–#N cards identical in size and structure so a future AI or
 *   engineer cannot invent a parallel card system and break the dashboard.
 *
 * HARD RULE — NEW RISK CARDS / HIT PANELS
 *   You MUST implement every new risk number (and every change to #1–#4) ONLY
 *   through the existing layout stack below. Do NOT create a new card shell,
 *   a new panel height, a custom grid of fields, or a one-off modal layout.
 *
 * REQUIRED STACK (outer → inner)
 *   1. RiskCaseCard + RISK_CASE_CARD
 *        One card per controller. Header: identity + risk tabs + LK.
 *        Outer size is locked (min-h-[40rem], overflow-hidden).
 *   2. RiskHitPanelLayout + RISK_HIT_LAYOUT
 *        ONLY allowed hit-panel shell. Fill slots; never copy shell classes.
 *        Slot order is owned here:
 *          riskNumber → badges → title → score → bar → feature → metrics
 *          → details → actions → note
 *   3. RiskHitFeatureBlock + RISK_HIT_FEATURE
 *        Feature strip is ALWAYS reserved (h-[9.25rem]). Put timeline /
 *        signal / forecast INSIDE this block only.
 *   4. RiskCaseDetailModal
 *        Table ℹ opens the SAME RiskCaseCard (tabs + layout). No alternate UI.
 *
 * ALLOWED WHEN ADDING RISK #N
 *   - New RiskNHitPanel.jsx that returns <RiskHitPanelLayout … slots … />
 *   - New RiskN*Feature.jsx rendered inside <RiskHitFeatureBlock>
 *   - Wire the hit into RiskCaseCard tab routing (buildCaseTabSlots)
 *   - Mirror files to company/Lk Frontend React/src/features/risks
 *   - Update matching *-preview.html / *-preview.jsx (same RiskCaseCard)
 *
 * FORBIDDEN
 *   - New root <article>/<div> card with its own min-height / padding / border
 *   - Putting FIO / address / tabs inside a hit panel
 *   - Growing the card from metrics/details/feature content
 *   - Skipping RiskHitPanelLayout “just for this risk”
 *   - A table-detail modal that is not RiskCaseDetailModal → RiskCaseCard
 *   - Changing RISK_* tokens in one frontend only (ARC and LK must match)
 *
 * SIZE LOCK
 *   RISK_CASE_CARD + RISK_HIT_LAYOUT + RISK_HIT_FEATURE own width/height.
 *   Slotted content adapts; it must never resize the shell.
 *
 * Both frontends: ai_risk_control/frontend and Lk features/risks.
 * Cursor rule: .cursor/rules/risk-card-mock-and-code.mdc
 * =============================================================================
 */

export const RISK_CARD_LAYOUT_CANON = Object.freeze({
  version: 1,
  caseShell: 'RiskCaseCard',
  hitShell: 'RiskHitPanelLayout',
  featureShell: 'RiskHitFeatureBlock',
  tableDetailShell: 'RiskCaseDetailModal',
  caseTokens: 'RISK_CASE_CARD',
  hitTokens: 'RISK_HIT_LAYOUT',
  featureTokens: 'RISK_HIT_FEATURE',
  slots: Object.freeze([
    'riskNumber',
    'badges',
    'title',
    'score',
    'bar',
    'feature',
    'metrics',
    'details',
    'actions',
    'note',
  ]),
});
